import { Chip, Icon, Link, Tooltip, Typography, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Check, ExitToApp, Remove, Warning } from '@material-ui/icons'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { getVisitsByFacilityId } from '../../actions/visits/visits.actions'
import { ScheduledVisitsTable } from '../../components/Tables/ScheduledVisitsTable'
import { getChipStyles, getDogIssues, getDepartingInfo } from '../../services/helpers'
import {
	STATUS_ID,
	checkIfNeedsRenewalAndShouldBeNotified,
	checkIfPetIsBanned,
	checkIfPetHasCompletedGrooming,
	getIconAndTagByFoundingAndMembership,
	timeFormat,
	GROOMING,
	countSignaturePlayPetsOnDate,
	countSignatureStayPetsOnDate,
	visitHasPetOnStayType,
	APPOINTMENT_TYPE_FILTERS,
	STAY_TYPES,
	VISIT_TYPES
} from '../../utils'
import useQuery from '../../hooks/useQuery'
import useSelectedFacility from '../../hooks/useSelectedFacility'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
	},
	petStatusIcon: {
		position: 'absolute',
		left: 0
	},
	membershipWrapper: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center'
	},
	membershipIcon: {
		color: '#6B4AD6',
		'&::before': {
			fontSize: '14px',
			lineHeight: '14px'
		}
	},
	membershipType: {
		fontFamily: 'Nunito',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '19px',
		color: '#777679',
		textTransform: 'capitalize'
	},
	cubbyContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '3px',
		width: '140px'
	}
}))

const Schedule = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const theme = useTheme()
	const { pushQueryParamToHistory, getQueryParam, replaceQueryParamInHistory } = useQuery()
	const { facility } = useSelectedFacility()

	let aggregates = {
		currently_in_facility: {
			value: 0
		},

		expected_daytime_dogs: {
			value: 0
		},
		expected_overnight_dogs: {
			value: 0
		},
		welcome_days: {
			value: 0
		},
		arriving_AM: {
			value: 0
		},
		arriving_PM: {
			value: 0
		},
		departing_AM: {
			value: 0
		},
		departing_PM: {
			value: 0
		},
		signature_stay_dogs: {
			value: 0
		},
		signature_play_dogs: {
			value: 0
		}
	}

	// const invalidSexes = ['Intact Male', 'Intact Female']

	const visits = useSelector((state) => state.specific.visits.visits)
	const selectedFilters = useSelector((state) => state.specific.visits.selectedFilters)

	const queryDate = getQueryParam('date')

	const [filteredVisits, setFilteredVisits] = useState([])
	const [date, setDate] = useState()

	const [formatted_date, setFormattedDate] = useState('')

	const [scheduleAggregates, setScheduleAggregates] = useState(aggregates)

	useEffect(() => {
		if (queryDate) {
			setDate(moment(queryDate).format('ll'))
		} else {
			setDate(moment().format('ll'))
			replaceQueryParamInHistory({ key: 'date', value: moment().format('YYYY-MM-DD') })
		}
	}, [queryDate])

	useEffect(() => {
		dispatch({ type: 'CLEAR_VISITS' })
	}, [])

	useEffect(() => {
		if (date) {
			setFormattedDate(moment(date).format('YYYY-MM-DD'))
		}
	}, [date])

	const getColumns = () => {
		return [
			{ title: 'Dates', render: (data) => renderDates(data, classes) },
			{
				width: 250,
				title: 'Details',
				render: (data) => renderDetails(data, classes),
				customSort: (a, b) => {
					let aTimeBlock
					let bTimeBlock
					if (!a.time_block) aTimeBlock = ''
					if (!b.time_block) bTimeBlock = ''
					return aTimeBlock.localeCompare(bTimeBlock)
				}
			},
			{
				title: 'Appointment',
				field: 'type',
				render: (data) => renderAppointment(data)
			},
			{
				title: 'Client',
				render: (data) => renderClientName(data),
				customFilterAndSearch: (value, rowData) => {
					let { household } = rowData
					let account = household.account || household.accounts.find((a) => a.primary)
					let name = value
					if (name) name = name.toUpperCase()
					return account ? account.full_name.toUpperCase().includes(name.toUpperCase()) : false
				},
				customSort: (a, b) => {
					if (a.household && b.household && a.household.accounts && b.household.accounts) {
						let accountA = a.household.accounts.find((a) => a.primary)
						let accountB = b.household.accounts.find((b) => b.primary)
						return accountA.full_name.localeCompare(accountB.full_name)
					}
				}
			},
			{
				title: 'Pet(s)',
				field: 'pets',
				render: (data) => renderPets(data, classes),
				customFilterAndSearch: (value, rowData) => {
					let { pets } = rowData
					let name = value
					if (name) name = name.toUpperCase()
					return pets && pets.length > 0
						? pets.findIndex((pet) => pet.name.toUpperCase().includes(name)) !== -1
						: false
				},
				customSort: (a, b) => {
					if (b.pets && b.pets.length > 0 && a.pets && a.pets.length > 0) {
						let petA = a.pets[0]
						let petB = b.pets[0]
						return petA.name.localeCompare(petB.name)
					}
				}
			},
			{
				title: 'Cubbies',
				render: (data) => {
					const { pets = [], cubbies: visitCubbies = [] } = data

					const sortedCubbies = []

					pets.sort((a, b) => {
						if (b.pets && b.pets.length > 0 && a.pets && a.pets.length > 0) {
							let petA = a.pets[0]
							let petB = b.pets[0]
							return petA.name.localeCompare(petB.name)
						}
					}).forEach((pet) => {
						const { _id } = pet
						const petCubbies = visitCubbies.find((cub) => String(cub.pet_id) === String(_id))
						sortedCubbies.push(petCubbies)
					})

					const {
						gear: gearFac,
						food: foodFac,
						boarding: boardingFac,
						medication: medicationFAc
					} = facility?.cubbies || {}

					const cubbies = []
					for (const petCubbies of sortedCubbies) {
						const cubbyRow = []
						for (const petCubby in petCubbies) {
							if (Object.hasOwn(petCubbies, petCubby)) {
								let prefix = ''
								if (petCubby === 'gear') {
									prefix = gearFac?.prefix || 'G'
								} else if (petCubby === 'food') {
									prefix = foodFac?.prefix || 'F'
								} else if (petCubby === 'medication') {
									prefix = medicationFAc?.prefix || 'M'
								} else if (petCubby === 'boarding') {
									prefix = boardingFac?.prefix || 'B'
								} else {
									continue
								}
								const cubbyNum = petCubbies[petCubby]
								if (cubbyNum !== null) {
									cubbyRow.push(`${prefix}${petCubbies[petCubby]}`)
								}
							}
						}
						cubbies.push(cubbyRow)
					}

					return cubbies.length > 0 ? renderCubbies(cubbies) : 'N/A'
				}
			},
			{
				title: 'Status',
				field: 'status.name',
				render: (data) => (
					<Chip
						label={data.status ? data.status.name : 'No Status'}
						style={data.status ? getChipStyles(data.status.name) : null}
					/>
				)
			},
			{
				title: 'Engagement',
				render: (data) => renderEngagement(data)
			}
		]
	}

	// Wait for the date (it starts unset) and send it as YYYY-MM-DD. Depend on the facility's
	// id, not the object, so a reloaded copy of the same facility doesn't refetch.
	useEffect(() => {
		if (facility?._id && date) {
			dispatch(getVisitsByFacilityId(facility, moment(date).format('YYYY-MM-DD')))
		}
	}, [date, facility?._id])

	const getNumberOfDepartingAMPets = () => {
		let departing = filteredVisits.filter((v) => {
			return (
				v.type === VISIT_TYPES.Boarding &&
				[STATUS_ID.Approved, STATUS_ID.ChangeRequested, STATUS_ID.InProgress].includes(v.status.id) &&
				v.dates[v.dates.length - 1] === formatted_date &&
				!v.last_day_daycare
			)
		})

		return departing.reduce((acc, visit) => acc + visit.pets.length, 0)
	}

	const getNumberOfDepartingPMPets = () => {
		let departing = filteredVisits.filter((v) => {
			if (![STATUS_ID.Approved, STATUS_ID.ChangeRequested, STATUS_ID.InProgress].includes(v.status.id)) return false
			if (v.type === VISIT_TYPES.CompatibilitySession) return true
			if (v.type === VISIT_TYPES.Daycare && v.dates[0] === formatted_date) return true
			if (v.type === VISIT_TYPES.Boarding && v.dates[v.dates.length - 1] === formatted_date && !!v.last_day_daycare)
				return true
			return false
		})

		return departing.reduce((acc, visit) => acc + visit.pets.length, 0)
	}

	const isDaytimeVisit = (vis) => {
		if (vis.type === VISIT_TYPES.Daycare) {
			return vis?.dates?.includes(formatted_date)
		} else if (vis.type === VISIT_TYPES.Boarding) {
			const dateIndex = vis.dates.findIndex((d) => d === formatted_date)
			if (dateIndex === 0) {
				return !!vis.first_day_daycare
			} else if (dateIndex === vis.dates.length - 1) {
				return !!vis.last_day_daycare
			} else {
				return true
			}
		}

		return true
	}

	const getNumberOfExpectedDaytimePets = () => {
		const daytimeStatusIds = [
			STATUS_ID.Approved,
			STATUS_ID.ChangeRequested,
			STATUS_ID.InProgress,
			STATUS_ID.Completed
		]
		let daytime = filteredVisits.filter((v) => daytimeStatusIds.includes(v?.status?.id) && !!isDaytimeVisit(v))

		return daytime.reduce((acc, visit) => acc + visit.pets.length, 0)
	}

	const getNumberOfExpectedOvernightPets = () => {
		let overnight = filteredVisits.filter(
			(v) =>
				!moment(v.endDate).isSame(date, 'date') &&
				v.status &&
				v.status.id &&
				(moment(v.endDate).isSame(date, 'date') ? v.status.id < STATUS_ID.Completed : v.status.id <= STATUS_ID.Completed) &&
				v.type === VISIT_TYPES.Boarding
		)

		return overnight.reduce((acc, visit) => acc + visit.pets.length, 0)
	}

	const getArriving = () => {
		return filteredVisits.filter((v) => {
			let start = moment(v.startDate)
			return v.status && v.status.id && v.status.id === STATUS_ID.Approved && start.isSame(date, 'date')
		})
	}

	const getCompatibility = () => {
		return filteredVisits.filter((visit) => visit.welcome_day_pets?.length)
	}

	const getNumberOfArrivingAm = () => {
		let arrivals = getArriving()

		return arrivals
			.filter(
				(v) =>
					v.type === VISIT_TYPES.Daycare ||
					!!v.first_day_daycare ||
					(v.type === VISIT_TYPES.CompatibilitySession && v.time_block === 'morning')
			)
			.reduce((acc, visit) => acc + visit.pets.length, 0)
	}

	const getNumberOfArrivingPm = () => {
		let arrivals = getArriving()

		return arrivals
			.filter(
				(v) =>
					(v.type === VISIT_TYPES.Boarding && !v.first_day_daycare) ||
					(v.type === VISIT_TYPES.CompatibilitySession && v.time_block !== 'morning')
			)
			.reduce((acc, visit) => acc + visit.pets.length, 0)
	}

	const getNumberOfInFacilityPets = () => {
		let inFacilty = getInFacility()

		return inFacilty.reduce(
			(acc, visit) =>
				acc +
				visit.pets.filter((pet) => pet && pet.status && pet.status.checked_in && !pet.status.checked_out)
					.length,
			0
		)
	}

	const getInFacility = () => {
		return filteredVisits.filter((visit) =>
			visit.pets.some((pet) => pet && pet.status && pet.status.checked_in && !pet.status.checked_out)
		)
	}

	const getNumberOfCompatibilityPets = () => {
		let compatibility = getCompatibility()

		return compatibility.reduce((acc, visit) => {
			let unique = [...new Set(visit.welcome_day_pets.map((id) => id.toString()))]

			return acc + unique.length
		}, 0)
	}

	/**
	 * True when the visit matches one selected "Appointment Type" filter value.
	 *
	 * @param {Object} visit
	 * @param {string} type - a visit type or an APPOINTMENT_TYPE_FILTERS value
	 * @returns {boolean}
	 */
	const matchesAppointmentTypeFilter = (visit, type) => {
		if (type === APPOINTMENT_TYPE_FILTERS.WelcomeDay) {
			return (
				(visit.type === VISIT_TYPES.Daycare && visit.welcome_day_pets?.length > 0) ||
				visit.type === VISIT_TYPES.CompatibilitySession
			)
		}

		if (type === APPOINTMENT_TYPE_FILTERS.SignatureStay) {
			return visitHasPetOnStayType(visit, VISIT_TYPES.Boarding, STAY_TYPES.SignatureStay)
		}

		if (type === APPOINTMENT_TYPE_FILTERS.SignaturePlay) {
			return visitHasPetOnStayType(visit, VISIT_TYPES.Daycare, STAY_TYPES.SignaturePlay)
		}

		return visit.type === type
	}

	const getNumberOfSignatureStayPets = () => {
		return countSignatureStayPetsOnDate(filteredVisits, formatted_date)
	}

	const getNumberOfSignaturePlayPets = () => {
		return countSignaturePlayPetsOnDate(filteredVisits, formatted_date)
	}

	useEffect(() => {
		if (visits) {
			let visitsFiltered = visits.filter((v) => {
				if (v.type === VISIT_TYPES.AddonOnly && v.subtype === GROOMING) {
					return false
				}
				return v.type !== VISIT_TYPES.Grooming
			})
			if (selectedFilters['arriving_today']) {
				visitsFiltered = visitsFiltered.filter((v) => moment(v.startDate).isSame(date, 'date'))
			}

			if (selectedFilters['departing_today']) {
				visitsFiltered = visitsFiltered.filter((v) => {
					return moment(v.type === VISIT_TYPES.Boarding ? v.endDate : v.startDate).isSame(date, 'date')
				})
			}

			if (selectedFilters['appointment_type'] && selectedFilters['appointment_type'].length) {
				visitsFiltered = visitsFiltered.filter((v) =>
					selectedFilters['appointment_type'].some((type) => matchesAppointmentTypeFilter(v, type))
				)
			}

			if (selectedFilters['appointment_status'] && selectedFilters['appointment_status'].length) {
				visitsFiltered = visitsFiltered.filter((v) =>
					v.status && v.status.id ? selectedFilters['appointment_status'].includes(v.status.id) : false
				)
			} else {
				// if no filter is selected we don't want to show cancelled
				visitsFiltered = visitsFiltered.filter(
					(v) => v.status && v.status.id && v.status.id !== STATUS_ID.Cancelled
				)
			}

			if (selectedFilters['drop_off_type'] && selectedFilters['drop_off_type'].length) {
				visitsFiltered = visitsFiltered.filter((v) => {
					const arriving =
						moment(v.dates[0]).isSame(date, 'date') && (v.status.id === STATUS_ID.Pending || v.status.id === STATUS_ID.Approved)

					if (arriving) {
						return selectedFilters['drop_off_type'].includes('early_dropoff') && v.early_dropoff
							? true
							: selectedFilters['drop_off_type'].includes('daycare_dropoff') &&
							  (v.type === VISIT_TYPES.Daycare || v.first_day_daycare) &&
							  !v.early_dropoff
							? true
							: selectedFilters['drop_off_type'].includes('boarding_dropoff') &&
							  v.type === VISIT_TYPES.Boarding &&
							  !v.first_day_daycare &&
							  !v.early_dropoff
							? true
							: false
					} else {
						return false
					}
				})
			}

			if (selectedFilters['pick_up_type'] && selectedFilters['pick_up_type'].length) {
				visitsFiltered = visitsFiltered.filter((v) => {
					let departing =
						v.status &&
						v.status.id &&
						v.status.id >= STATUS_ID.Approved &&
						v.status.id < STATUS_ID.Completed &&
						moment(v.type === VISIT_TYPES.Boarding ? v.endDate : v.startDate).isSame(date, 'date')

					if (departing) {
						return selectedFilters['pick_up_type'].includes('early') &&
							v.type === VISIT_TYPES.Boarding &&
							!v.last_day_daycare
							? true
							: selectedFilters['pick_up_type'].includes('standard') &&
							  (v.type === VISIT_TYPES.Daycare ||
									v.type === VISIT_TYPES.CompatibilitySession ||
									(v.type === VISIT_TYPES.Boarding && v.last_day_daycare)) &&
							  !v.extended_pickup
							? true
							: selectedFilters['pick_up_type'].includes('late') && v.extended_pickup
							? true
							: false
					} else {
						return false
					}
				})
			}

			if (selectedFilters.hasAlerts) {
				visitsFiltered = visitsFiltered.filter((v) => {
					return v.pets.some((pet) => {
						let issues = getDogIssues(pet, v)

						return issues && issues.length ? true : false
					})
				})
			}

			setFilteredVisits(visitsFiltered)
		}
	}, [selectedFilters, visits])

	useEffect(() => {
		let updatedAggregates = cloneDeep(scheduleAggregates)

		if (filteredVisits.length > 0) {
			updatedAggregates.currently_in_facility.value = getNumberOfInFacilityPets()
			updatedAggregates.expected_daytime_dogs.value = getNumberOfExpectedDaytimePets()
			updatedAggregates.expected_overnight_dogs.value = getNumberOfExpectedOvernightPets()
			updatedAggregates.welcome_days.value = getNumberOfCompatibilityPets()
			updatedAggregates.arriving_AM.value = getNumberOfArrivingAm()
			updatedAggregates.arriving_PM.value = getNumberOfArrivingPm()
			updatedAggregates.departing_AM.value = getNumberOfDepartingAMPets()
			updatedAggregates.departing_PM.value = getNumberOfDepartingPMPets()
			updatedAggregates.signature_stay_dogs.value = getNumberOfSignatureStayPets()
			updatedAggregates.signature_play_dogs.value = getNumberOfSignaturePlayPets()
			setScheduleAggregates(updatedAggregates)
		} else {
			for (const key in updatedAggregates) {
				if (updatedAggregates.hasOwnProperty(key)) {
					if (typeof updatedAggregates[key] === 'object') {
						updatedAggregates[key].value = 0
					}
				}
			}
			setScheduleAggregates(updatedAggregates)
		}
	}, [filteredVisits])

	const changeDate = (type) => {
		if (type === 'add') {
			const forwardDate = moment(date).add(1, 'd')
			pushQueryParamToHistory({ key: 'date', value: forwardDate.format('YYYY-MM-DD') })
			setDate(forwardDate.format('ll'))
		} else if (type === 'subtract') {
			const backDate = moment(date).subtract(1, 'd')
			pushQueryParamToHistory({ key: 'date', value: backDate.format('YYYY-MM-DD') })
			setDate(backDate.format('ll'))
		}
	}

	const renderDates = (data) => (
		<Typography variant="body2">
			{data.startDate && data.endDate && data.type === 'boarding'
				? `${moment(data.startDate).format('MMM DD')} - ${moment(data.endDate).format('MMM DD, YYYY')}`
				: moment(data.startDate).format('MMM DD, YYYY')}
		</Typography>
	)

	const renderDetails = (data) => {
		let everyPetHasStatus = data.pet_ids.every((id) => data.pet_status.find((s) => s.pet_id === id))
		let hasArrivingPets =
			data.status && data.status.id && data.status.id > STATUS_ID.InProgress
				? false
				: !everyPetHasStatus ||
				  (everyPetHasStatus &&
						moment(data.startDate).isSame(date, 'date') &&
						data.pet_status.some((s) => !s.checked_in && !s.checked_out))

		let headingText = ''
		let timeText = ''
		let dropOffBegin =
			data.type === 'boarding' && !data.first_day_daycare
				? '17:00'
				: facility?.services?.daycare?.dropoff_begins
		let dropOffDeadline =
			data.type === 'boarding' && !data.first_day_daycare
				? '21:00'
				: facility?.services?.daycare?.dropoff_deadline
		let pickUpDeadline =
			data.type === 'boarding' && !data.last_day_daycare
				? facility?.services?.boarding?.pickup_deadline
				: facility?.services?.daycare?.pickup_deadline

		if (hasArrivingPets) {
			let arrival = 'Standard'

			if (data.early_dropoff) {
				arrival = 'Early'
			}

			if (data.type === 'boarding' && !data.first_day_daycare) {
				arrival = 'Overnight'
			}

			headingText = `Arriving ${arrival}`
			if (data.early_dropoff) {
				dropOffBegin = '06:00'
				dropOffDeadline = '07:00'
			} else if (data.time_block === 'afternoon') {
				dropOffBegin = '12:00'
				dropOffDeadline = '14:00'
			} else if (data.time_block === 'evening') {
				dropOffBegin = '18:30'
				dropOffDeadline = '19:30'
			}
			timeText =
				dropOffBegin && dropOffDeadline
					? `${moment(dropOffBegin, 'HH:mm').format(timeFormat(dropOffBegin))} - ${moment(
							dropOffDeadline,
							'HH:mm'
					  ).format(timeFormat(dropOffDeadline))}`
					: ''
		}

		let hasDepartingPets = false
		const prms = {
			headingText,
			timeText,
			date,
			pickUpDeadline
		}

		;({ timeText, headingText, hasDepartingPets } = getDepartingInfo(data, prms))

		if (hasDepartingPets) {
			let departure = 'Standard'
			if (data.type === 'boarding' && !data.last_day_daycare) {
				departure = 'AM'
			} else if ((data.type === 'daycare' || data.last_day_daycare) && !data.extended_pickup) {
				departure = 'Standard'
			} else if ((data.type === 'daycare' || data.last_day_daycare) && !!data.extended_pickup) {
				departure = 'Late'
			}
			headingText = `Departing ${departure}`
		}

		return (
			<div style={{ width: 160 }}>
				<Fragment>
					{headingText ? <Typography variant="caption">{headingText}</Typography> : null}
					{timeText ? (
						<Typography variant="caption" style={{ color: '#777679', display: 'block' }}>
							{timeText}
						</Typography>
					) : null}
				</Fragment>
			</div>
		)
	}

	const renderCubbies = (data) => {
		if (data && data.length) {
			return (
				<div className={classes.cubbyContainer}>
					{data.map((row) => (
						<span>{row.join('-')}</span>
					))}
				</div>
			)
		}
	}

	const renderPets = (data, classes) => (
		<div>
			{data.pets && data.pets.length > 0
				? data.pets.map((pet) => {
						let issues = getDogIssues(pet, data)

						return (
							<div
								key={pet._id}
								style={{
									display: 'flex',
									alignItems: 'center',
									position: 'relative',
									justifyContent: 'space-between',
									paddingLeft: 32
								}}>
								{pet.status && pet.status.checked_in ? (
									<Check
										className={classes.petStatusIcon}
										style={{
											fill: '#4CAF50',
											height: 16,
											strokeWidth: 3,
											stroke: '#4CAF50'
										}}
									/>
								) : pet.status && pet.status.checked_in === false ? (
									<ExitToApp
										className={classes.petStatusIcon}
										style={{
											fill: '#777679',
											height: 16,
											strokeWidth: 1,
											stroke: '#777679'
										}}
									/>
								) : (
									<Remove
										className={classes.petStatusIcon}
										style={{
											fill: '#777679',
											height: 16,
											strokeWidth: 1,
											stroke: '#777679'
										}}
									/>
								)}
								<Link
									component={RouterLink}
									style={{
										color: '#1A1A35'
									}}
									onClick={(e) => e.stopPropagation()}
									to={`/clients/${data.household._id}/pets/${pet._id}`}>
									{pet.name + ' '}{' '}
									{pet.sex
										? pet.sex == 'Intact Male' || pet.sex == 'Neutered Male'
											? '(M)'
											: pet.sex == 'Intact Female' || pet.sex == 'Spayed Female'
											? '(F)'
											: ''
										: ''}
								</Link>
								<div style={{ marginLeft: '15px' }}>
									{issues && issues.length ? (
										<Tooltip title={renderToolTip(issues)}>
											<Chip
												label={<Warning style={{ fontSize: '15px' }} />}
												style={
													issues.some((i) => i.type === 'danger')
														? {
																...getChipStyles('rejected'),
																...{ paddingTop: '6px' }
														  }
														: {
																...getChipStyles('warning'),
																...{ paddingTop: '6px' }
														  }
												}
											/>
										</Tooltip>
									) : null}
								</div>
							</div>
						)
				  })
				: null}
		</div>
	)

	const renderToolTip = (issues) => {
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{issues.map((i, index) => (
					<ToolTipItem key={index} message={i.message} color={i.color || null} />
				))}
			</div>
		)
	}

	const ToolTipItem = (props) => {
		let { message, color } = props
		return (
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<div
					style={{
						height: '9px',
						width: '9px',
						backgroundColor: `${color ? color : '#EB4C4C'}`,
						borderRadius: '50%',
						display: 'inline-block',
						marginRight: '16px'
					}}
				/>
				<Typography style={{ fontWeight: 600, fontSize: '14px' }}>{message}</Typography>
			</div>
		)
	}

	const handleAppRightClick = (e, prms) => {
		e.preventDefault()
		const url = `/clients/${prms.household_id}/appointments/${prms._id}${`?date=${prms.date}`}`
		const win = window.open(url, '_blank')
		win.focus()
	}

	const renderAppointment = (data) => {
		const formattedDate = moment(date).format('YYYYMMDD')
		const appPrms = {
			_id: data._id,
			household_id: data.household_id,
			date: formattedDate
		}

		const isWelcomeDay = data?.welcome_day_pets?.length

		return (
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-start',
					alignItems: 'center'
				}}>
				<Typography
					onContextMenu={(e) => handleAppRightClick(e, appPrms)}
					style={{
						textTransform: 'capitalize',
						color: '#6B4AD6',
						fontWeight: 800,
						fontSize: '16px',
						lineHeight: '22px'
					}}>
					{data.type === 'compatibility_session' ? 'Compatibility Session' : data.type}
				</Typography>

				{isWelcomeDay ? (
					<Icon className="fa fa-calendar" style={{ fontSize: 14, color: '#6B4AD6', marginLeft: 8 }} />
				) : null}
			</div>
		)
	}

	const renderClientName = (data) => {
		if (data.household && data.household.accounts) {
			const account = data.household.accounts.find((a) => a.primary)
			const [iconName, iconColor, tag] = getIconAndTagByFoundingAndMembership(data.household)

			return (
				<div>
					<Link
						component={RouterLink}
						style={{
							color: theme.palette.secondary.main,
							marginBottom: 5,
							fontSize: '14px',
							lineHeight: '19px'
						}}
						onClick={(e) => e.stopPropagation()}
						to={`/clients/${data.household._id}/contacts`}>
						{account ? account.full_name : 'No name'}
					</Link>
					{!!iconName || !!tag ? (
						<div className={classes.membershipWrapper}>
							{!!iconName && (
								<Icon
									className={`fa fa-${iconName}`}
									style={{
										color: iconColor,
										fontWeight: '900',
										fontSize: '14px',
										lineHeight: '16px',
										width: 20
									}}
								/>
							)}
							<Typography className={classes.membershipType}>{tag}</Typography>
						</div>
					) : null}
				</div>
			)
		}

		return 'No client'
	}

	const renderEngagement = (visit) => {
		let bannedFromBarAndPark = false
		let bannedFromPetCare = false
		let hasCompletedGrooming = false
		let bannedFromGrooming = false
		const [needsRenewal, shouldBeNotified] = checkIfNeedsRenewalAndShouldBeNotified(
			visit?.household,
			visit?.household?.memberships
		)

		if (visit.pets && visit.pets.length) {
			bannedFromBarAndPark =
				visit && visit.pets && visit.pets.some((p) => checkIfPetIsBanned(p, 'Bar & Park'))
			bannedFromPetCare =
				visit && visit.pets && visit.pets.some((p) => checkIfPetIsBanned(p, 'Daycare & Boarding'))
			hasCompletedGrooming = visit && visit.pets && visit.pets.some((p) => checkIfPetHasCompletedGrooming(p))
			bannedFromGrooming = visit && visit.pets && visit.pets.some((p) => checkIfPetIsBanned(p, 'Grooming'))
		}

		const getIconColor = (banned) => (banned ? '#DB4A42' : '#007A4B')
		const getTextColor = (banned) => (banned ? '#DC2A2A' : '#007A4B')

		return (
			<Fragment>
				{(visit.household && !visit.household.hasUsedBarAndPark) || bannedFromBarAndPark ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${bannedFromBarAndPark ? 'times' : 'seedling'}`}
							style={{
								color: getIconColor(bannedFromBarAndPark),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromBarAndPark),
								width: '100%',
								display: 'block'
							}}>
							Bar & Park
						</Typography>
					</div>
				) : null}
				{(visit.household && !visit.household.hasUsedPetCare) || bannedFromPetCare ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${bannedFromPetCare ? 'times' : 'seedling'}`}
							style={{
								color: getIconColor(bannedFromPetCare),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromPetCare),
								width: '100%',
								display: 'block'
							}}>
							Pet Care
						</Typography>
					</div>
				) : null}
				{!hasCompletedGrooming ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-seedling`}
							style={{
								color: '#007A4B',
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: '#007A4B',
								width: '100%',
								display: 'block'
							}}>
							Grooming
						</Typography>
					</div>
				) : null}
				{bannedFromGrooming ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-times`}
							style={{
								color: getIconColor(bannedFromGrooming),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromGrooming),
								width: '100%',
								display: 'block'
							}}>
							Grooming
						</Typography>
					</div>
				) : null}
				{needsRenewal && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${shouldBeNotified ? 'bell' : 'bell-slash'}`}
							style={{
								color: shouldBeNotified ? '#0074C8' : '#A2A1A4',
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: shouldBeNotified ? '#0074C8' : '#A2A1A4',
								width: '100%',
								display: 'block'
							}}>
							Needs Renewal
						</Typography>
					</div>
				)}
			</Fragment>
		)
	}

	return (
		<div className={classes.root}>
			{facility ? (
				<div className={classes.visitsTableContainer}>
					<ScheduledVisitsTable
						data={filteredVisits}
						columns={getColumns()}
						scheduleAggregates={scheduleAggregates}
						date={date}
						changeDate={changeDate}
						setDate={setDate}
					/>
				</div>
			) : (
				<h3>Please Select a Facility</h3>
			)}
		</div>
	)
}

export default Schedule
